import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, LogOut, Menu, X, User } from "lucide-react";
import { useState } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const dashboardPath =
    user?.role === "admin" ? "/admin" : user?.role === "instructor" ? "/instructor" : "/dashboard";

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      data-testid={`nav-${to.replace(/\//g, "-")}`}
      onClick={() => setOpen(false)}
      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        loc.pathname === to ? "text-naranja" : "text-white/90 hover:text-white"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="bg-marino sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 text-white">
              <img src="/shield-ompec.svg" alt="OM-PEC" className="h-11 w-auto object-contain" />
              <div className="hidden sm:flex flex-col leading-tight border-l border-white/20 pl-2.5">
                <span className="font-heading font-black text-lg tracking-tight text-white">OM-PEC</span>
                <span className="text-[10px] uppercase tracking-wider text-naranja font-semibold">Protegemos personas · fortalecemos empresas</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <NavLink to="/">Inicio</NavLink>
              <NavLink to="/catalog">Cursos</NavLink>
              <NavLink to="/redeem">Activar código</NavLink>
              {user && <NavLink to={dashboardPath}>Mi panel</NavLink>}
            </nav>

            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <User className="w-4 h-4" />
                    <span data-testid="user-name">{user.name}</span>
                    <span className="text-naranja text-xs uppercase font-semibold">{user.role}</span>
                  </div>
                  <button
                    data-testid="logout-btn"
                    onClick={() => { logout(); nav("/"); }}
                    className="ml-2 bg-gris text-marino px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover transition-colors"
                  >
                    <LogOut className="w-4 h-4 inline mr-1" /> Salir
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="login-nav-btn"
                    className="bg-gris text-marino px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover transition-colors"
                  >Iniciar sesión</Link>
                  <Link
                    to="/register"
                    data-testid="register-nav-btn"
                    className="bg-naranja text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover transition-colors"
                  >Registrarme</Link>
                </>
              )}
            </div>

            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="md:hidden text-white p-2"
              aria-label="menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden bg-marino border-t border-white/10 px-4 py-3 space-y-2">
            <NavLink to="/">Inicio</NavLink>
            <NavLink to="/catalog">Cursos</NavLink>
            <NavLink to="/redeem">Activar código</NavLink>
            {user && <NavLink to={dashboardPath}>Mi panel</NavLink>}
            <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
              {user ? (
                <button
                  data-testid="logout-mobile-btn"
                  onClick={() => { logout(); nav("/"); setOpen(false); }}
                  className="bg-gris text-marino px-4 py-2 rounded-lg text-sm font-medium"
                >Cerrar sesión</button>
              ) : (
                <>
                  <Link onClick={() => setOpen(false)} to="/login" className="bg-gris text-marino text-center px-4 py-2 rounded-lg text-sm font-medium">Iniciar sesión</Link>
                  <Link onClick={() => setOpen(false)} to="/register" className="bg-naranja text-white text-center px-4 py-2 rounded-lg text-sm font-medium">Registrarme</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-marino text-white/80 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <img src="/shield-ompec.svg" alt="OM-PEC" className="h-14 w-auto object-contain" />
              <span className="font-heading font-black text-lg text-white">OM-PEC</span>
            </div>
            <p className="text-sm italic text-naranja/90 font-medium mb-2">"Protegemos personas, fortalecemos empresas."</p>
            <p className="text-sm">Centro de Entrenamiento e-Learning Automatizado. Formación profesional certificada.</p>
          </div>
          <div>
            <h4 className="font-heading font-bold text-white mb-3">Plataforma</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-naranja">Catálogo de cursos</Link></li>
              <li><Link to="/redeem" className="hover:text-naranja">Activar código</Link></li>
              <li><Link to="/login" className="hover:text-naranja">Iniciar sesión</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-bold text-white mb-3">Contacto</h4>
            <p className="text-sm">soporte@ompec.com</p>
            <p className="text-sm mt-1">© {new Date().getFullYear()} OM-PEC. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
